import { AbstractModel } from '@/models/abstractModel';
import { CountryDictionaryItem } from '@/models/dictionary/country';
import { leagueService } from '@/services/leagueService';
import { useCountryDictionary, leagueLimitsCategoryDictionary, leagueCategoryDictionary, leagueGenderDictionary, leagueAgeCategoryDictionary } from '@/stores';
import type { ILeagueApi } from '@/types';
import { useI18n } from 'vue-i18n';
import { Locale } from '@/constants';
import { useLeagueStore } from '@/stores/league';

export class LeagueItem extends AbstractModel {
    public countryId: number | null;
    public clId: number;
    public gameCountDball: number;
    public gameCountLive: number;
    public iso3: string | null;
    public name: string;
    public limitsCategoryId: number;
    public categoryId: number | null;
    public ageCategoryId: number;
    public tier: number | null;
    public gender: number;

    constructor(data: ILeagueApi) {
        super();
        this.countryId = data.country_id;
        this.clId = data.clId;
        this.gameCountDball = data.game_count_dball;
        this.gameCountLive = data.game_count_live;
        this.iso3 = data.iso3;
        this.name = data.name;
        this.limitsCategoryId = data.limits_category_id;
        this.categoryId = data.category_id;
        this.ageCategoryId = data.age_category_id;
        this.tier = data.tier;
        this.gender = data.gender;
    }

    get genderName(): string {
        return this._lazyLoad('genderName', this.gender, () => {
            return leagueGenderDictionary.nameById(this.gender);
        });
    }

    get limitsCategoryName(): string {
        return this._lazyLoad('limitsCategoryName', this.limitsCategoryId, () => {
            return leagueLimitsCategoryDictionary.nameById(this.limitsCategoryId);
        });
    }

    get categoryName(): string {
        return this._lazyLoad('categoryName', this.categoryId, () => {
            if (this.categoryId === null) {
                return '';
            }
            return leagueCategoryDictionary.nameById(this.categoryId);
        });
    }

    get ageCategoryName(): string {
        return this._lazyLoad('ageCategoryName', this.ageCategoryId, () => {
            return leagueAgeCategoryDictionary.nameById(this.ageCategoryId);
        });
    }

    get tierName(): string {
        if (this.tier === null) {
            return '';
        }

        const { locale } = useI18n();
        if (locale.value === Locale.Portuguese) {
            return `${this.tier}º`;
        }

        switch (this.tier) {
            case 1:
                return `${this.tier}st`;
            case 2:
                return `${this.tier}nd`;
            case 3:
                return `${this.tier}rd`;
            default:
                return `${this.tier}th`;
        }
    }

    get countryName(): string {
        return this._lazyLoad('countryName', this.countryId, () => {
            const country = this.getCountry();
            if (country !== null) {
                return country.name;
            }
            const { t } = useI18n();
            return t('league.world').toUpperCase();
        });
    }

    public getCountry(): CountryDictionaryItem | null {
        if (this.countryId === null) {
            return null;
        }
        const store = useCountryDictionary();
        return store.itemById(this.countryId);
    }

    public isProcessing(): boolean {
        const store = useLeagueStore();
        return this.clId in store.favoriteLeagueProcessing;
    }

    public isFavorite(): boolean {
        const store = useLeagueStore();
        return this.clId in store.favoriteLeague;
    }

    public toggleFavorite(): void {
        if (this.isProcessing()) {
            return;
        }

        if (this.isFavorite()) {
            void leagueService.removeFavorite(this.clId);
        } else {
            void leagueService.addFavorite(this.clId);
        }
    }
}
